import React, { useEffect } from 'react';

export type AlertType = 'success' | 'error' | 'info' | 'warning';

export interface AlertProps {
  type: AlertType;
  message: string;
  isOpen: boolean;
  onClose: () => void;
  onRetry?: () => void;
  autoClose?: boolean;
  duration?: number;
}

const getAlertStyles = (type: AlertType) => {
  switch (type) {
    case 'success':
      return {
        bg: 'bg-green-50',
        text: 'text-green-600',
        icon: 'ri-checkbox-circle-line',
        border: 'border-green-200'
      };
    case 'error':
      return {
        bg: 'bg-gray-50',
        text: 'text-black-600',
        icon: 'ri-error-warning-line',
        border: 'border-red-200'
      };
    case 'info':
      return {
        bg: 'bg-white',
        text: 'text-black-600',
        icon: 'ri-information-line',
        border: 'border-gray-200'
      };
    case 'warning':
      return {
        bg: 'bg-yellow-50',
        text: 'text-yellow-600',
        icon: 'ri-alert-line',
        border: 'border-yellow-200'
      };
  }
};

const Alert: React.FC<AlertProps> = ({
  type,
  message,
  isOpen,
  onClose,
  onRetry,
  autoClose = false,
  duration = 5000
}) => {
  const styles = getAlertStyles(type);

  useEffect(() => {
    let timerId: number | null = null;

    if (isOpen && autoClose) {
      timerId = window.setTimeout(() => {
        onClose();
      }, duration);
    }

    return () => {
      if (timerId !== null) {
        clearTimeout(timerId);
      }
    };
  }, [isOpen, autoClose, duration, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 px-4">
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className={`${styles.bg} ${styles.text} ${styles.border} border p-0 rounded-lg shadow-lg max-w-md w-full z-10 animate-fadeIn`}
      >
        <div className="flex items-start">
          <div className="flex-1">
            <div className="mb-1 pt-4 text-md font-bold text-gray-600 text-center">
              알림
            </div>
            <div className="mb-2 p-4 text-md font-medium text-gray-600 text-center">
              {message}
            </div>
            <div className="flex justify-center space-x-2 p-4 border-t bg-gray-200">
              {onRetry ? (
                <button
                  className={`${styles.text} underline font-medium`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRetry();
                    onClose();
                  }}
                >
                  확인
                </button>
              ) : (
                <button
                  className="text-gray-500 hover:text-gray-700"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                  }}
                >
                  닫기
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Alert;